import { ReactNode } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import { useNamespace } from '@/hooks/useNamespace';

type Namespace = 'common' | 'auth' | 'legal' | 'navigation' | 'listings' | 'offers' | 'profile' | 'admin' | 'platform' | 'allocations';

interface NamespaceLoaderProps {
  namespaces: Namespace[];
  children: ReactNode;
  /** Optional skeleton to show while loading. If not provided, children render immediately with fallback keys. */
  skeleton?: ReactNode;
}

/**
 * Optional wrapper to show a skeleton while namespaces load.
 * 
 * Usage (opt-in skeleton):
 * ```tsx
 * <NamespaceLoader 
 *   namespaces={['listings', 'navigation']} 
 *   skeleton={<PageSkeleton />}
 * >
 *   <ListingsContent />
 * </NamespaceLoader>
 * ```
 * 
 * Usage (no skeleton, fallback keys shown briefly):
 * ```tsx
 * <NamespaceLoader namespaces={['listings', 'navigation']}>
 *   <ListingsContent />
 * </NamespaceLoader>
 * ```
 * 
 * Note: If ready=true immediately (namespaces cached), skeleton never appears.
 */
export function NamespaceLoader({ namespaces, children, skeleton }: NamespaceLoaderProps) {
  const { ready } = useNamespace(namespaces);

  // If no skeleton provided, always render children (keys as fallback)
  if (!skeleton) {
    return <>{children}</>;
  }

  // Show skeleton only while loading
  if (!ready) {
    return <>{skeleton}</>;
  }

  return <>{children}</>;
}

/**
 * Simple page skeleton for common use cases.
 * Customize or create your own for specific pages.
 */
export function PageSkeleton({ className }: { className?: string }) {
  return (
    <div className={className ?? "space-y-6 p-6"}>
      <Skeleton className="h-8 w-48" />
      <div className="space-y-3">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <Skeleton className="h-32 w-full" />
    </div>
  );
}
